/**
 * Which floor the Overview opens on (twin slice 1, plan W3 step 11).
 *
 * "Wherever a robot is" -- but only a robot that has really said where it
 * is. Three records look like a floor and are not one:
 *
 *   - the worker's roster STUB, which has no level (or an empty one);
 *   - the adapter's LOST SENTINEL, a pose of exactly (0, 0) on a real
 *     level: it names a floor the robot may never have been on;
 *   - a record carrying an `unavailable_reason`: whatever pose rides
 *     along with the reason is not one the robot reported (the same rule
 *     `robot-receipts.noteRobot` applies to ghosts).
 *
 * And for a site audience, a SIMULATED robot (`isSimulatedFleet`): the
 * hotel never sees the mock fleet, so the map must not open on the floor
 * only the mock robot is on. Ops audiences see it, and may open there.
 *
 * WHERE A ROBOT WAS LAST SEEN (Akash, 2026-10-06): with no robot saying
 * where it is now -- offline, unavailable, or a stub after a restart --
 * the floor is the one the recorder last saw a robot on
 * (`/robots/last_poses`, the newest real pose of any robot, as far back
 * as the site keeps poses), never "the bottom of the building" while a
 * robot's last floor is known. The same simulated-fleet rule applies.
 *
 * Liveness is deliberately NOT asked. The seed fires within the first
 * seconds after load, when every robot is still "checking"; a robot's
 * shape is all there is to go on by then.
 */
import type { RobotStateView } from '../../api/rmf-client';
import type { RobotLastPose } from '../../api/twin';
import { realPoseOf } from '../../api/robot-receipts';
import { isSimulatedFleet } from '../../state/simulated-fleet';

/** The floor of the first robot with a real reported pose on one of
 *  `levels`, in the order the robots arrive; else the floor of the newest
 *  `recorded` last pose on one of them; null when there is neither. */
export function seedFloorFromRobots(
  robots: readonly Pick<RobotStateView, 'fleet' | 'location' | 'unavailableReason'>[] | undefined,
  levels: readonly string[],
  showSimulated: boolean,
  recorded?: Readonly<Record<string, RobotLastPose>>,
): string | null {
  return seedFloorFromLive(robots, levels, showSimulated) ?? lastSeenFloor(recorded, levels, showSimulated);
}

/** The floor of the robot the recorder saw most recently, among those on
 *  one of `levels`; null when it has none. Newest by the worker's own
 *  stamp, compared only with other stamps from that same clock. */
export function lastSeenFloor(
  recorded: Readonly<Record<string, RobotLastPose>> | undefined,
  levels: readonly string[],
  showSimulated: boolean,
): string | null {
  let best: RobotLastPose | null = null;
  for (const pose of Object.values(recorded ?? {})) {
    if (!showSimulated && isSimulatedFleet(pose.fleet)) continue;
    if (!levels.includes(pose.level)) continue;
    if (!Number.isFinite(pose.t)) continue;
    if (best === null || pose.t > best.t) best = pose;
  }
  return best ? best.level : null;
}

function seedFloorFromLive(
  robots: readonly Pick<RobotStateView, 'fleet' | 'location' | 'unavailableReason'>[] | undefined,
  levels: readonly string[],
  showSimulated: boolean,
): string | null {
  for (const robot of robots ?? []) {
    if (!showSimulated && isSimulatedFleet(robot.fleet)) continue;
    if (robot.unavailableReason) continue;
    // `realPoseOf` refuses the stub (no level) and the (0, 0) sentinel;
    // the stamp is not used here.
    const pose = realPoseOf(robot.location, 0);
    if (pose && levels.includes(pose.map)) return pose.map;
  }
  return null;
}
